import { isObject } from '#object/is-object';

/**
 * Determines if a value is defined (not `undefined` or `null`).
 * Optionally, if the value is an object, checks that specified properties are also defined.
 *
 * @typeParam T - The type of the value to check.
 * @param value - The value to be checked.
 * @param checkProperties - If `true` and `value` is an object, also check that specified properties are defined.
 * @param properties - The properties of the object to check for being defined. If omitted, all properties are checked.
 * @returns `true` if the value (and optionally its properties) are defined, otherwise `false`.
 *
 * @example
 * ```typescript
 * // Check if a value is defined
 * const value = { name: 'John', age: 30 };
 * console.log(isDefined(value)); // true
 * // Check if a value is defined and has specific properties
 * console.log(isDefined(value, true, 'name', 'age')); // true
 * // Check if a value is defined but has an undefined property
 * console.log(isDefined(value, true, 'name', 'address')); // false
 */
export const isDefined = <T = unknown>(
  value: T | null | undefined,
  checkProperties = false,
  ...properties: (keyof T)[]
): value is NonNullable<T> => {
  const defined = value !== undefined && value !== null;

  if (!defined) {
    return false;
  }

  if (!isObject(value) || !checkProperties) {
    return defined;
  }

  const keys = properties.length === 0 ? (Object.keys(value) as (keyof T)[]) : properties;

  return keys.every((key) => {
    const propertyValue = value[key];

    return typeof propertyValue !== 'undefined';
  });
};
